import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, Mail } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_culinary-connect-37/artifacts/dowryrls_582627c1-c0bf-47f6-ab36-5cc8b78702e3.png";
const API = process.env.REACT_APP_BACKEND_URL;

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setMenuOpen(false);
  };

  const handleResend = async () => {
    setResending(true); setResendMsg("");
    try {
      await axios.post(`${API}/api/auth/resend-verification`,
        { host_url: window.location.origin }, { withCredentials: true });
      setResendMsg("Sent — check your inbox");
    } catch (e) {
      setResendMsg(e.response?.data?.detail || "Failed to send");
    } finally {
      setResending(false);
    }
  };

  const showVerifyBanner = user && user.email_verified === false;

  const navLinks = [
    { to: "/find-a-chef", label: "Find a Chef" },
    { to: "/register?role=chef", label: "List Your Services" },
  ];

  return (
    <>
    <nav
      data-testid="navbar"
      className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#050505]/90 border-b border-[#D4AF37]/20"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img src={LOGO} alt="Are You A Chef" className="h-10 w-10 rounded-full object-cover" />
          <span className="font-serif text-white text-lg tracking-wide hidden sm:block">
            Are You A Chef<span className="text-[#D4AF37]">.</span>com
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className="text-[#A0A0A0] hover:text-[#D4AF37] text-sm tracking-wide transition-colors duration-200"
            >
              {link.label}
            </Link>
          ))}
          {user && user.role === "admin" && (
            <Link to="/admin" className="text-[#A0A0A0] hover:text-[#D4AF37] text-sm tracking-wide transition-colors duration-200">
              Admin
            </Link>
          )}
        </div>

        {/* Desktop Auth */}
        <div className="hidden md:flex items-center gap-4">
          {!user ? (
            <>
              <Link to="/login" data-testid="nav-login-btn"
                className="text-[#A0A0A0] hover:text-white text-sm tracking-wide transition-colors duration-200">
                Login
              </Link>
              <Link to="/register" data-testid="nav-register-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] px-5 py-2 text-sm font-medium tracking-wide transition-colors duration-200">
                Register
              </Link>
            </>
          ) : (
            <>
              {user.role === "chef" && (
                <Link to="/chef/dashboard" data-testid="nav-dashboard-btn"
                  className="text-[#A0A0A0] hover:text-[#D4AF37] text-sm tracking-wide transition-colors duration-200">
                  My Dashboard
                </Link>
              )}
              <span className="text-[#D4AF37] text-sm">{user.name}</span>
              <button onClick={handleLogout} data-testid="nav-logout-btn"
                className="border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] px-4 py-1.5 text-sm tracking-wide transition-all duration-200">
                Logout
              </button>
            </>
          )}
        </div>

        {/* Mobile menu toggle */}
        <button className="md:hidden text-white" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0A0A0A] border-t border-[#D4AF37]/10 px-6 py-4 flex flex-col gap-4">
          {navLinks.map(link => (
            <Link key={link.to} to={link.to} onClick={() => setMenuOpen(false)}
              className="text-[#A0A0A0] hover:text-[#D4AF37] text-sm tracking-wide py-2">
              {link.label}
            </Link>
          ))}
          {user ? (
            <>
              {user.role === "chef" && (
                <Link to="/chef/dashboard" onClick={() => setMenuOpen(false)}
                  className="text-[#A0A0A0] hover:text-[#D4AF37] text-sm tracking-wide py-2">My Dashboard</Link>
              )}
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setMenuOpen(false)}
                  className="text-[#A0A0A0] hover:text-[#D4AF37] text-sm tracking-wide py-2">Admin</Link>
              )}
              <button onClick={handleLogout}
                className="text-left text-[#D4AF37] text-sm tracking-wide py-2">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMenuOpen(false)}
                className="text-[#A0A0A0] hover:text-[#D4AF37] text-sm tracking-wide py-2">Login</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)}
                className="bg-[#D4AF37] text-black px-5 py-2 text-sm font-medium text-center">Register</Link>
            </>
          )}
        </div>
      )}
    </nav>
    {showVerifyBanner && (
      <div data-testid="verify-email-banner"
        className="fixed top-16 w-full z-40 bg-[#D4AF37]/10 backdrop-blur-xl border-b border-[#D4AF37]/30 px-6 py-2.5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-[#D4AF37] text-xs sm:text-sm flex items-center gap-2">
            <Mail size={14} className="flex-shrink-0" />
            <span>Please verify your email to unlock chef contact details.</span>
          </p>
          <div className="flex items-center gap-3">
            {resendMsg && <span className="text-[#A0A0A0] text-xs">{resendMsg}</span>}
            <button
              onClick={handleResend}
              disabled={resending}
              data-testid="navbar-resend-verify-btn"
              className="text-xs tracking-widest uppercase border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-3 py-1.5 transition-colors disabled:opacity-40"
            >
              {resending ? "Sending..." : "Resend Email"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
